import React, { useState, useEffect } from 'react';
import './Navigation.css';
import useRaceStore from '../../store/raceStore';
// API fonksiyonları

/**
 * Navigation component for the boat tracking application
 * Displays the app name, connection status and control options
 */
const Navigation = () => {
  // API durumunu ve son güncelleme zamanını state olarak tutuyoruz
  const [apiStatus, setApiStatus] = useState("connected"); // connected, timeout, error
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState("");
  const [timeSinceUpdate, setTimeSinceUpdate] = useState("");
  
  // Store'dan veri çekme fonksiyonunu al
  const fetchData = useRaceStore(state => state.fetchData);
  const storeLastUpdate = useRaceStore(state => state.lastUpdate);
  
  // Son güncelleme zamanını göster ve düzenli olarak geçen dakikayı güncelle
  useEffect(() => {
    if (storeLastUpdate) {
      setLastUpdate(storeLastUpdate);
      
      // Başlangıçta ve düzenli olarak geçen dakikayı hesapla
      const updateElapsedTime = () => {
        const now = new Date();
        const diff = Math.floor((now - new Date(storeLastUpdate)) / 60000); // dakika cinsinden fark
        setTimeSinceUpdate(`(${diff} dk önce)`);
      };
      
      // İlk hesaplama
      updateElapsedTime();
      
      // Her 10 saniyede bir geçen süreyi güncelle
      const timer = setInterval(updateElapsedTime, 10000); // 10 saniye
      
      return () => clearInterval(timer);
    }
  }, [storeLastUpdate]);
  
  // Ortam kontrolü - Vercel'de çalışıyorsa veya HTTPS ise proxy kullan
  const isVercelEnvironment = typeof window !== 'undefined' && window.location.hostname.includes('vercel.app');
  const isHttpsEnvironment = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const isProduction = isVercelEnvironment || isHttpsEnvironment;
  
  // Yenile butonuna tıklandığında çağrılacak fonksiyon - tamamen yeni yaklaşım
  const handleRefresh = async () => {
    setRefreshing(true);
    setMessage("");
    setApiStatus("connecting"); // Bağlantı kuruluyor durumuna geç
    
    try {
      // API endpoint'i - üretim ortamında proxy kullan, geliştirme ortamında doğrudan API'ye bağlan
      const timestamp = new Date().getTime();
      const randomStr = Math.random().toString(36).substring(7);
      const apiUrl = isProduction
        ? `/api/race?nocache=${timestamp}&r=${randomStr}`
        : `http://mavivatan.imarine.com.tr:3583/api/v1/race?nocache=${timestamp}&r=${randomStr}`;
      
      console.log('Yenile butonu: API isteği yapılıyor (doğrudan):', apiUrl);
      
      // Fetch API ile istek yap - önbellekleme olmadan
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store' // Fetch API önbellekleme kontrolü
      });
      
      if (!response.ok) {
        throw new Error(`API isteği başarısız: ${response.status}`);
      }
      
      // JSON yanıtı al
      const data = await response.json();
      console.log('Yenile butonu: API yanıtı:', data);
      
      // API yanıtının doğru yapıda olup olmadığını kontrol et
      if (!data || !data.race || !data.tracks || !data.tracks.length) {
        throw new Error('API yanıtı geçerli veri içermiyor');
      }
      
      // API durumunu güncelle
      setApiStatus("connected");
      
      if (data) {
        // Doğrudan store'u güncelle
        await fetchData();
        
        // Son güncelleme zamanını güncelle
        const now = new Date();
        setLastUpdate(now);
        
        // Zaman farkını güncelle
        setTimeSinceUpdate("0 dk önce");
        
        // Son veri ile yeni veriyi karşılaştır
        const currentBoat = useRaceStore.getState().currentBoat;
        const newTrack = data.tracks[0];
        
        // Veri değişti mi kontrol et
        if (currentBoat && (
          currentBoat.lat !== newTrack.lat ||
          currentBoat.lon !== newTrack.lon ||
          currentBoat.sog_kn !== newTrack.sog_kn ||
          currentBoat.cog_deg !== newTrack.cog_deg ||
          currentBoat.distance_m !== newTrack.distance_m
        )) {
          // Veri değişti
          setMessage("Veriler güncellendi, yeni konum bilgisi alındı.");
        } else {
          // Veri aynı
          setMessage("Veri kontrol edildi, konum değişmemiş.");
        }
        
        // API yanıtını detaylı olarak logla
        console.log('Yenile butonu: Veri başarıyla güncellendi:', {
          zaman: now.toLocaleTimeString(),
          konum: [data.tracks[0].lat, data.tracks[0].lon],
          hız: data.tracks[0].sog_kn,
          yön: data.tracks[0].cog_deg
        });
      } else {
        setMessage("Veri alınamadı.");
      }
    } catch (error) {
      console.error("Veri güncellenirken hata:", error);
      console.error('Hata detayları:', {
        mesaj: error.message,
        kod: error.code,
        url: error.url,
        stack: error.stack
      });
      
      // Kullanıcıya daha detaylı hata mesajı göster
      setMessage(`Veri güncellenirken hata: ${error.message}`);
      setApiStatus("error");
      
      // Mock veri kullanmayı dene - gerçek API başarısız olduğunda
      try {
        console.log('API hatası nedeniyle mock veri kullanılıyor...');
        const mockData = require('../../data/mockData').mockApiResponse;
        
        if (mockData && mockData.race && mockData.tracks && mockData.tracks.length > 0) {
          // Store'u mock veri ile güncelle
          await fetchData();
          
          // Son güncelleme zamanını güncelle
          const now = new Date();
          setLastUpdate(now);
          
          // Zaman farkını güncelle
          setTimeSinceUpdate("0 dk önce");
          
          setMessage("API hatası nedeniyle test verileri gösteriliyor.");
          setApiStatus("connected_fallback");
        }
      } catch (mockError) {
        console.error('Mock veri yükleme hatası:', mockError);
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <nav className="navigation">
      <div className="nav-logo">
        <h1>Linea Rossa</h1>
        <span className="subtitle">Türkiye Turu Rekoru Canlı Takip</span>
      </div>
      
      <div className="connection-status">
        <div className={`status-indicator ${apiStatus}`}></div>
        <div className="status-details">
          <div className="status-row">
            <div className="status-item">
              <div className="status-label">API Durumu:</div>
              <div className="status-value">{apiStatus === "connected" ? "Bağlantı aktif" : apiStatus === "connecting" ? "Bağlanıyor..." : apiStatus === "timeout" ? "Zaman Aşımı" : "Hata"}</div>
            </div>
            
            <div className="status-item">
              <div className="status-label">Son Güncelleme:</div>
              <div className="status-value">
                {lastUpdate ? lastUpdate.toLocaleTimeString() : ""}
                {timeSinceUpdate && <span className="time-since-update">{timeSinceUpdate}</span>}
              </div>
            </div>
          </div>
          
          {message && <div className="update-message">{message}</div>}
        </div>
      </div>
      
      <div className="nav-controls">
        <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing}>
          <span className="refresh-icon">↻</span> {refreshing ? "Yenileniyor..." : "Yenile"}
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
